import { useNotificationStore } from '@/stores/notifications';
import React, { ReactNode, useCallback, useState, MouseEvent } from 'react';
import { Tr, Td, Text, useDisclosure, Flex, Box, Tooltip, Spinner } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { AssignQuizToUsersRequest } from '../../types';
import { DeleteQuizModal } from '@/features/Quiz/components/DeleteQuizModal';
import { DistributeQuizModal } from '@/features/Quiz/components/DistributeQuizModal';
import Icon from '@/components/Icon';
import { format } from 'date-fns';
import { AdminQuizV2ListItem } from '@/features/Quiz/types';
import { shareAdminQuizV2 } from '@/features/Quiz/api/getQuizzes';
import InstitutionsModal from '@/features/Playlists/components/InstitutionsModal';
import { InstitutionModalTypes } from '@/features/Playlists';
import { useAdminQuizV2Scorm } from '@/api/scormApi';
import { useAdminQuizV2Cc } from '@/api/ccApi';

type Props = {
    quiz: AdminQuizV2ListItem;
};

const TextEl = ({ children }: { children: ReactNode }) => (
    <Text color="black" lineHeight={'21px'} textAlign="center" display="inline-block" p="0 10px" w={'auto'}>
        {children}
    </Text>
);

export const AdminQuizItemV2 = ({ quiz }: Props) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isInstitutionsOpen, onOpen: onInstitutionsOpen, onClose: onInstitutionsClose } = useDisclosure();
    const [institutionModalType, setInstitutionModalType] = useState<InstitutionModalTypes>(null);
    const { addNotification } = useNotificationStore();

    const [error, setError] = useState('');
    const [openDistributeQuiz, setOpenDistributeQuiz] = useState(false);

    const { isLoading: isScormLoading, handleScormDownload } = useAdminQuizV2Scorm({
        quizUuid: quiz.quizUuid,
        playlistTitle: quiz.title ?? '',
    });

    const { isLoading: isCCLoading, handleCCDownload } = useAdminQuizV2Cc({
        quizUuid: quiz.quizUuid,
        title: quiz.title ?? '',
    });

    const handleOpenInstitutionsScorm = () => {
        setInstitutionModalType('scorm');
        onInstitutionsOpen();
    };

    const handleOpenInstitutionsCc = () => {
        setInstitutionModalType('cc');
        onInstitutionsOpen();
    };

    const handleDistributeQuizClose = useCallback(() => {
        setOpenDistributeQuiz(false);
        setError('');
    }, []);

    const handleDistributeQuizOpen = useCallback((e: MouseEvent) => {
        e.preventDefault();
        setOpenDistributeQuiz(true);
    }, []);

    const handleDistributeQuizSubmit = useCallback(
        async (data: AssignQuizToUsersRequest) => {
            try {
                const res = await shareAdminQuizV2(quiz.quizUuid, { emails: data.emails });
                const notFound = (res as { notFound?: string[] }).notFound ?? [];
                const notAccess = (res as { notAccess?: string[] }).notAccess ?? [];
                const shared = (res as { sharedQuizzes?: { sharedWithUserEmail?: string | null }[] }).sharedQuizzes ?? [];
                const already = (res as { alreadySharedQuizzes?: { sharedWithUserEmail?: string | null }[] })
                    .alreadySharedQuizzes ?? [];

                if (notFound.length) {
                    addNotification({
                        type: 'error',
                        title: `Not found: ${notFound.join(', ')}`,
                    });
                }
                if (notAccess.length) {
                    addNotification({
                        type: 'error',
                        title: `Not access: ${notAccess.join(', ')}`,
                    });
                }
                if (already.length) {
                    const emails = already.map((r) => r.sharedWithUserEmail).filter(Boolean);
                    if (emails.length) {
                        addNotification({
                            type: 'error',
                            title: `Already shared: ${emails.join(', ')}`,
                        });
                    }
                }
                if (shared.length) {
                    const emails = shared.map((r) => r.sharedWithUserEmail).filter(Boolean);
                    if (emails.length) {
                        addNotification({
                            type: 'success',
                            title: `Shared: ${emails.join(', ')}`,
                        });
                    }
                }
                setError('');
            } catch (e: unknown) {
                const message = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : '';
                setError(message);
                throw e;
            }
        },
        [addNotification, quiz.quizUuid]
    );

    return (
        <>
            <InstitutionsModal
                isOpen={isInstitutionsOpen}
                onClose={onInstitutionsClose}
                handleScormDownload={handleScormDownload}
                handleCCDownload={handleCCDownload}
                isLoading={isScormLoading || isCCLoading}
                type={institutionModalType}
            />
            <DeleteQuizModal isOpen={isOpen} onClose={onClose} quiz={quiz} quizType="adminV2" />
            <DistributeQuizModal
                isOpen={openDistributeQuiz}
                onClose={handleDistributeQuizClose}
                onSubmit={handleDistributeQuizSubmit}
                quiz={quiz}
                errorMessage={error}
                sharedList={null}
            />
            <Tr>
                <Td w={'65%'} py={'10px'}>
                    <Flex as={Link} to={`/quiz/admin-quiz-v2/view/${quiz.quizUuid}`} py={'10px'}>
                        <Text color={'black'} lineHeight={'21px'}>
                            {quiz.title}
                        </Text>
                    </Flex>
                </Td>

                <Td w={'10%'}>
                    <Text color={'black'} lineHeight={'21px'}>
                        -
                    </Text>
                </Td>
                <Td w={'15%'} textAlign="center">
                    <TextEl>{quiz.noOfQuestions ? `${quiz.noOfQuestions} Questions` : '-'}</TextEl>
                </Td>
                <Td w={'15%'} textAlign="center">
                    <TextEl>{quiz?.endTime ? format(new Date(quiz.endTime), 'MM/dd/yyyy') : '-'}</TextEl>
                </Td>
                <Td w="auto">
                    <Flex columnGap={'10px'} align="center" justifyContent={'space-around'}>
                        <Box
                            variant="playlistSmBtn"
                            borderRadius={'16px'}
                            height={'28px'}
                            onClick={handleDistributeQuizOpen}>
                            <Tooltip hasArrow label={'Share quiz'} aria-label={'Share quiz'}>
                                <span>
                                    <Icon highlightOnHover icon="share-from-square" />
                                </span>
                            </Tooltip>
                        </Box>
                        <Box as={Link} to={`/quiz/admin-quiz-v2/edit/${quiz.quizUuid}`} borderRadius={'16px'} height={'28px'}>
                            <Tooltip hasArrow label={'Edit quiz'} aria-label={'Edit quiz'}>
                                <span>
                                    <Icon highlightOnHover icon="pencil" />
                                </span>
                            </Tooltip>
                        </Box>

                        <Box as={Link} to={`/quiz/admin-quiz-v2/copy/${quiz.quizUuid}`} borderRadius={'16px'} height={'28px'}>
                            <Tooltip hasArrow label={'Copy quiz'} aria-label={'Copy quiz'}>
                                <span>
                                    <Icon highlightOnHover icon="clone" />
                                </span>
                            </Tooltip>
                        </Box>
                        <Box onClick={handleOpenInstitutionsScorm}>
                            <Tooltip hasArrow label={'Download SCORM'} aria-label={'Download SCORM'}>
                                {isScormLoading ? (
                                    <Spinner size={'xs'} />
                                ) : (
                                    <span>
                                        <Icon highlightOnHover icon="file-zipper" />
                                    </span>
                                )}
                            </Tooltip>
                        </Box>
                        <Box onClick={handleOpenInstitutionsCc}>
                            <Tooltip hasArrow label={'Download CC'} aria-label={'Download CC'}>
                                {isCCLoading ? (
                                    <Spinner size={'xs'} />
                                ) : (
                                    <span>
                                        <Icon fontSize={'20px'} highlightOnHover icon="closed-captioning" />
                                    </span>
                                )}
                            </Tooltip>
                        </Box>
                        <Box onClick={onOpen} fontSize={'0.875rem'} fontWeight={500} cursor={'pointer'}>
                            <Tooltip hasArrow label={'Delete quiz'} aria-label={'Delete quiz'}>
                                <span>
                                    <Icon forDelete highlightOnHover icon="trash" />
                                </span>
                            </Tooltip>
                        </Box>
                    </Flex>
                </Td>
            </Tr>
        </>
    );
};
